import { useRef } from 'react';
import { devLog } from '../utils/devLogger';

/**
 * Scope Filter Hook (matches 1.0 scopeFilter.js)
 * Handles conditional show/hide of fields based on dropdown values
 * 
 * ✅ OPTIMIZED: Caches DOM refs on init, no repeated querySelector on every event
 */
export const useScopeFilter = () => {
  // Keep cleanup fns so re-initializing doesn't stack listeners
  const cleanupFnsRef = useRef<Array<() => void>>([]);

  // Cache refs for all scope filter elements (populated on init)
  const cacheRef = useRef<{
    directorsReport: {
      select: HTMLSelectElement | null;
      fields: Map<number, { name: HTMLElement | null; type: HTMLElement | null; id: HTMLElement | null }>;
    };
    statementByDirectors: {
      select: HTMLSelectElement | null;
      fields: Map<number, { name: HTMLElement | null; type: HTMLElement | null; id: HTMLElement | null; disclosure: HTMLElement | null }>;
    };
    applicationSubmission: {
      select: HTMLSelectElement | null;
      statutoryField: HTMLElement | null;
      courtField: HTMLElement | null;
    };
    auditStatus: {
      select: HTMLSelectElement | null;
      exemptionField: HTMLElement | null;
    };
    accountingStandards: {
      select: HTMLSelectElement | null;
      otherField: HTMLElement | null;
    };
    exemption: {
      select: HTMLSelectElement | null;
      relatedFields: HTMLElement[];
    };
  }>({
    directorsReport: { select: null, fields: new Map() },
    statementByDirectors: { select: null, fields: new Map() },
    applicationSubmission: { select: null, statutoryField: null, courtField: null },
    auditStatus: { select: null, exemptionField: null },
    accountingStandards: { select: null, otherField: null },
    exemption: { select: null, relatedFields: [] }
  });
  
  const initializeScopeFilters = () => {
    // Clear any previous listeners
    cleanupFnsRef.current.forEach(fn => {
      try { fn(); } catch { /* noop */ }
    });
    cleanupFnsRef.current = [];

    // Cache all elements ONCE on init
    cacheElements();

    // Set up lightweight event handlers that use cached refs
    setupDirectorsReportHandler();
    setupStatementByDirectorsHandler();
    setupApplicationSubmissionHandler();
    setupAuditStatusHandler();
    setupAccountingStandardsHandler();
    setupExemptionHandler();
    
    devLog('✅ Scope filters initialized (with cached refs)');
  };

  // Cache all DOM elements once on initialization
  const cacheElements = () => {
    const cache = cacheRef.current;

    // Directors Report
    cache.directorsReport.select = document.querySelector('select[name="NumberOfDirectorsSigningDirectorsReport"]');
    const drContainer = document.getElementById('120000_DisclosureOnDirectorsReportAbstract');
    if (drContainer) {
      const numbers = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth'];
      for (let i = 1; i <= 5; i++) {
        const directorNumber = numbers[i];
        cache.directorsReport.fields.set(i, {
          name: drContainer.querySelector(`[name="NameOf${directorNumber}DirectorWhoSignedDirectorsReport"]`)?.closest('.form-group') as HTMLElement || null,
          type: drContainer.querySelector(`[name="TypeOfIdentificationOf${directorNumber}DirectorWhoSignedDirectorsReport"]`)?.closest('.form-group') as HTMLElement || null,
          id: drContainer.querySelector(`[name="IdentificationNumberOf${directorNumber}DirectorWhoSignedDirectorsReport"]`)?.closest('.form-group') as HTMLElement || null
        });
      }
    }

    // Statement by Directors
    cache.statementByDirectors.select = document.querySelector('select[name="NumberOfDirectorsSigningStatementByDirectors"]');
    const sbdContainer = document.getElementById('120100_DisclosureOnStatementByDirectorsAbstract');
    if (sbdContainer) {
      const numbers = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth'];
      for (let i = 1; i <= 5; i++) {
        const directorNumber = numbers[i];
        cache.statementByDirectors.fields.set(i, {
          name: sbdContainer.querySelector(`[name="NameOf${directorNumber}DirectorWhoSignedStatementByDirectors"]`)?.closest('.form-group') as HTMLElement || null,
          type: sbdContainer.querySelector(`[name*="TypeOfIdentification"][name*="${directorNumber}"][name*="StatementByDirectors"]`)?.closest('.form-group') as HTMLElement || null,
          id: sbdContainer.querySelector(`[name="IdentificationNumberOf${directorNumber}DirectorWhoSignedStatementByDirectors"]`)?.closest('.form-group') as HTMLElement || null,
          disclosure: sbdContainer.querySelector(`[name*="Disclosure"][name*="${directorNumber}"][name*="PrimarilyResponsible"]`)?.closest('.form-group') as HTMLElement || null
        });
      }
    }

    // Application Submission
    cache.applicationSubmission.select = document.querySelector('select[name="ApplicationOfSubmission"]');
    cache.applicationSubmission.statutoryField = document.querySelector('[name="StatutoryDeclarationForRectification"]')?.closest('.form-group') as HTMLElement || null;
    cache.applicationSubmission.courtField = document.querySelector('[name="CourtOrderReferenceNumber"]')?.closest('.form-group') as HTMLElement || null;

    // Audit Status
    cache.auditStatus.select = document.querySelector('select[name="DisclosureOfFinancialStatementsAuditStatus"]');
    cache.auditStatus.exemptionField = document.querySelector('select[name="AuditExemptionCategory"]')?.closest('.form-group') as HTMLElement || null;

    // Accounting Standards
    cache.accountingStandards.select = document.querySelector('select[name="BasisOfAccountingStandardsAppliedToPrepareFinancialStatements"]');
    cache.accountingStandards.otherField = document.querySelector('[name="DisclosureOfOtherAccountingStandardsApplied"]')?.closest('.form-group') as HTMLElement || null;

    // Exemption Fields
    cache.exemption.select = document.querySelector('select[name="DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister"]');
    const relatedFieldNames = [
      'DescriptionOnWhetherCompanyHadAppliedForExemptionFromCoincidingForeignSubsidiaryFinancialYearEndWithHoldingCompany',
      'DescriptionOnWhetherCompanyHadAppliedFromFilingFinancialStatementsAndReportsInFullXBRLFormat',
      'DescriptionOnWhetherCompanyHadAppliedToWaiveLodgementOfForeignStatementsByForeignCompany',
      'DescriptionOnWhetherCompanyHadAppliedForReliefFromRequirementsAsToFormAndContentsOfDirectorsReport',
      'DescriptionOnWhetherCompanyHadAppliedForReliefFromRequirementsAsToFormAndContentsOfFinancialStatements',
      'DescriptionOnWhetherCompanyHadAppliedForExtensionOfTimeForCirculationOfFinancialStatementsAndReports',
      'DescriptionOnWhetherCompanyHadAppliedForExtensionOfTimeToLodgeFinancialStatementsAndReports',
      'DescriptionOnWhetherCompanyHadAppliedForExtensionOfTimeForHoldingAnnualGeneralMeeting',
      'DescriptionOnWhetherCompanyHadAppliedForExtensionOfTimeToLodgeAnnualReturn',
      'DescriptionOnWhetherCompanyHadAppliedAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardsToFinancialStatementsAndReportsOrAnnualReturnToMinister'
    ];
    cache.exemption.relatedFields = relatedFieldNames
      .map(name => document.querySelector(`select[name="${name}"]`)?.closest('.form-group') as HTMLElement)
      .filter(Boolean);
  };

  // Directors Report - uses cached refs (no querySelector on update!)
  const setupDirectorsReportHandler = () => {
    const cache = cacheRef.current.directorsReport;
    
    const updateVisibility = () => {
      if (!cache.select) return;
      
      const selectedNumber = parseInt(cache.select.value) || 0;
      
      // Hide all first (using cached refs - no DOM query!)
      for (let i = 1; i <= 5; i++) {
        const fields = cache.fields.get(i);
        if (fields) {
          if (fields.name) fields.name.style.display = 'none';
          if (fields.type) fields.type.style.display = 'none';
          if (fields.id) fields.id.style.display = 'none';
        }
      }
      
      // Show selected (using cached refs - no DOM query!)
      for (let i = 1; i <= selectedNumber && i <= 5; i++) {
        const fields = cache.fields.get(i);
        if (fields) {
          if (fields.name) fields.name.style.display = '';
          if (fields.type) fields.type.style.display = '';
          if (fields.id) fields.id.style.display = '';
        }
      }
    };
    
    const onChange = (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      if (target === cache.select) {
        updateVisibility();
      }
    };
    
    document.addEventListener('change', onChange, true);
    document.addEventListener('formDataLoaded', updateVisibility as EventListener);
    updateVisibility();

    cleanupFnsRef.current.push(() => {
      document.removeEventListener('change', onChange, true);
      document.removeEventListener('formDataLoaded', updateVisibility as EventListener);
    });
  };

  // Statement by Directors - uses cached refs
  const setupStatementByDirectorsHandler = () => {
    const cache = cacheRef.current.statementByDirectors;
    
    const updateVisibility = () => {
      if (!cache.select) return;
      
      const selectedNumber = parseInt(cache.select.value) || 0;
      
      // Hide all first
      for (let i = 1; i <= 5; i++) {
        const fields = cache.fields.get(i);
        if (fields) {
          if (fields.name) fields.name.style.display = 'none';
          if (fields.type) fields.type.style.display = 'none';
          if (fields.id) fields.id.style.display = 'none';
          if (fields.disclosure) fields.disclosure.style.display = 'none';
        }
      }
      
      // Show selected
      for (let i = 1; i <= selectedNumber && i <= 5; i++) {
        const fields = cache.fields.get(i);
        if (fields) {
          if (fields.name) fields.name.style.display = '';
          if (fields.type) fields.type.style.display = '';
          if (fields.id) fields.id.style.display = '';
          if (fields.disclosure) fields.disclosure.style.display = '';
        }
      }
    };
    
    const onChange = (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      if (target === cache.select) {
        updateVisibility();
      }
    };
    
    document.addEventListener('change', onChange, true);
    document.addEventListener('formDataLoaded', updateVisibility as EventListener);
    updateVisibility();

    cleanupFnsRef.current.push(() => {
      document.removeEventListener('change', onChange, true);
      document.removeEventListener('formDataLoaded', updateVisibility as EventListener);
    });
  };

  // Application Submission - uses cached refs
  const setupApplicationSubmissionHandler = () => {
    const cache = cacheRef.current.applicationSubmission;
    
    const updateVisibility = () => {
      if (!cache.select) return;
      
      const selectedValue = cache.select.value;
      
      // Hide both first
      if (cache.statutoryField) cache.statutoryField.style.display = 'none';
      if (cache.courtField) cache.courtField.style.display = 'none';
      
      switch(selectedValue) {
        case 'Ordinary filing':
          // Both hidden
          break;
        case 'Rectification filing':
          if (cache.statutoryField) cache.statutoryField.style.display = '';
          break;
        case 'Rectification filing with court order':
        case 'Court order filing':
          if (cache.courtField) cache.courtField.style.display = '';
          break;
      }
    };
    
    const onChange = (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      if (target === cache.select) {
        updateVisibility();
      }
    };
    
    document.addEventListener('change', onChange, true);
    document.addEventListener('formDataLoaded', updateVisibility as EventListener);
    updateVisibility();

    cleanupFnsRef.current.push(() => {
      document.removeEventListener('change', onChange, true);
      document.removeEventListener('formDataLoaded', updateVisibility as EventListener);
    });
  };

  // Audit Status - uses cached refs
  const setupAuditStatusHandler = () => {
    const cache = cacheRef.current.auditStatus;
    
    const updateVisibility = () => {
      if (!cache.select || !cache.exemptionField) return;
      
      const selectedValue = cache.select.value;
      
      if (selectedValue === 'Unaudited') {
        cache.exemptionField.style.display = '';
      } else {
        cache.exemptionField.style.display = 'none';
      }
    };
    
    const onChange = (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      if (target === cache.select) {
        updateVisibility();
      }
    };
    
    document.addEventListener('change', onChange, true);
    document.addEventListener('formDataLoaded', updateVisibility as EventListener);
    updateVisibility();

    cleanupFnsRef.current.push(() => {
      document.removeEventListener('change', onChange, true);
      document.removeEventListener('formDataLoaded', updateVisibility as EventListener);
    });
  };

  // Accounting Standards - uses cached refs
  const setupAccountingStandardsHandler = () => {
    const cache = cacheRef.current.accountingStandards;
    
    const updateVisibility = () => {
      if (!cache.select || !cache.otherField) return;
      
      const selectedValue = cache.select.value;
      
      if (selectedValue === 'Others') {
        cache.otherField.style.display = '';
      } else {
        cache.otherField.style.display = 'none';
      }
    };
    
    const onChange = (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      if (target === cache.select) {
        updateVisibility();
      }
    };
    
    document.addEventListener('change', onChange, true);
    document.addEventListener('formDataLoaded', updateVisibility as EventListener);
    updateVisibility();

    cleanupFnsRef.current.push(() => {
      document.removeEventListener('change', onChange, true);
      document.removeEventListener('formDataLoaded', updateVisibility as EventListener);
    });
  };

  // Exemption Fields - uses cached refs
  const setupExemptionHandler = () => {
    const cache = cacheRef.current.exemption;
    
    const updateVisibility = () => {
      if (!cache.select) return;
      
      const selectedValue = cache.select.value;
      const shouldShow = selectedValue === 'Yes';
      
      cache.relatedFields.forEach(field => {
        if (field) {
          field.style.display = shouldShow ? '' : 'none';
          // Reset value when hiding
          if (!shouldShow) {
            const fieldSelect = field.querySelector('select') as HTMLSelectElement;
            if (fieldSelect) fieldSelect.value = '';
          }
        }
      });
    };
    
    const onChange = (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      if (target === cache.select) {
        updateVisibility();
      }
    };
    
    document.addEventListener('change', onChange, true);
    document.addEventListener('formDataLoaded', updateVisibility as EventListener);
    updateVisibility();

    cleanupFnsRef.current.push(() => {
      document.removeEventListener('change', onChange, true);
      document.removeEventListener('formDataLoaded', updateVisibility as EventListener);
    });
  };
  
  return {
    initializeScopeFilters
  };
};
